import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    WarningAmber as WarningIcon
} from '@mui/icons-material';
import { apiClient } from '@libs/apiRequest';

const PopulatePermissionDialog = ({ open, onClose }) => {
    const [deleteExisting, setDeleteExisting] = useState(false);
    const [deleteIfExists, setDeleteIfExists] = useState(false);
    const [loading, setLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState(null);

    const handlePopulate = async () => {
        setLoading(true);
        setResponseMessage(null);
        try {
            const response = await apiClient.get('/populate-default/permissions', {
                params: {
                    'delete-existing': deleteExisting,
                    'delete-if-exists': deleteIfExists
                }
            });
            setResponseMessage({
                type: 'success',
                text: response?.data?.message || 'Permissions populated successfully!'
            });
        } catch (error) {
            setResponseMessage({
                type: 'error',
                text: error?.response?.data?.message || error?.message || 'Failed to populate permissions.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setDeleteExisting(false);
            setDeleteIfExists(false);
            setResponseMessage(null);
            onClose();
        }
    };

    const bothChecked = deleteExisting && deleteIfExists;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Populate Permissions</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={deleteExisting}
                                onChange={(e) => setDeleteExisting(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="delete-existing"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={deleteIfExists}
                                onChange={(e) => setDeleteIfExists(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="delete-if-exists"
                    />

                    {bothChecked && (
                        <Alert severity="warning" icon={<WarningIcon />}>
                            <Typography variant="body2">
                                <strong>Caution:</strong> Checking both options can overwrite existing permissions. Please proceed with caution and ensure you understand the impact of this action.
                            </Typography>
                        </Alert>
                    )}

                    {!bothChecked && (
                        <Typography variant="body2" color="text.secondary">
                            Checking both options can overwrite existing permissions. Proceed with caution.
                        </Typography>
                    )}

                    {responseMessage && (
                        <Alert severity={responseMessage.type}>
                            {responseMessage.text}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handlePopulate}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? 'Populating...' : 'Populate'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PopulatePermissionDialog;
